import { AlertTriangleIcon, InboxIcon, RotateCcwIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { TableCell, TableRow } from '@app/components/ui/table'
import { cn } from '@app/lib/cn'
import { formatAmount } from './format'
import type { CariBalance, CariType } from './api'

/* ── Sign convention ───────────────────────────────────────────────────────────
 *
 * balance = borç − alacak.
 *   POSITIVE → the account owes US (alacaklıyız)   → zeytin / "ok"
 *   NEGATIVE → WE owe the account (borçluyuz)      → hardal / "warn"
 *
 * Kordovan is reserved for primary actions across this app (see the orders
 * module), so the payable tone is hardal, not kordovan. The legend under the
 * list table spells this out for the owners — the two colours are the whole
 * point of the screen.
 */

export function balanceToneClass(balance: number): string {
  if (balance > 0) return 'text-zeytin'
  if (balance < 0) return 'text-hardal'
  return 'text-ink-muted/60'
}

/** Balance in mono, right-aligned, coloured by direction. Zero renders as an em dash. */
export function BalanceAmount({
  balance,
  currency,
  className,
}: {
  balance: number
  currency: string
  className?: string
}) {
  if (balance === 0) {
    return <span className={cn('text-ink-muted/60', className)}>—</span>
  }
  return (
    <span
      className={cn('whitespace-nowrap font-mono tnum', balanceToneClass(balance), className)}
      title={balance > 0 ? 'Bizden alacaklı değil — bize borçlu' : 'Bu cariye borçluyuz'}
    >
      {formatAmount(balance, currency)}
    </span>
  )
}

/**
 * One line per currency, never wrapping mid-amount — a two- or three-currency
 * account already takes several lines here, and letting a single amount
 * wrap on top of that pushed rows taller than their neighbours. Balances are
 * never summed across currencies.
 */
export function BalanceStack({ balances }: { balances: readonly CariBalance[] }) {
  const moving = balances.filter((b) => b.balance !== 0)
  if (moving.length === 0) {
    return <span className="text-ink-muted/60">—</span>
  }
  return (
    <span className="flex flex-col items-end gap-0.5 leading-tight">
      {moving.map((b) => (
        <BalanceAmount key={b.currency} balance={b.balance} currency={b.currency} />
      ))}
    </span>
  )
}

/* ── Badges ───────────────────────────────────────────────────────────────── */

const TYPE_TONE = {
  customer: 'ok',
  supplier: 'info',
  other: 'neutral',
} as const

export function CariTypeBadge({ type, typeName }: { type: CariType; typeName: string }) {
  return (
    <Badge
      tone={TYPE_TONE[type]}
      title={`Cari kodu ön eki: ${type === 'customer' ? '120' : type === 'supplier' ? '320' : '335 / 360'}`}
    >
      {typeName}
    </Badge>
  )
}

/* ── State screens ────────────────────────────────────────────────────────── */

export function TableSkeleton({ rows = 8, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <TableRow key={i} aria-hidden>
          {Array.from({ length: cols }, (__, j) => (
            <TableCell key={j}>
              <span
                className="block h-2.5 animate-pulse rounded-xs bg-hairline"
                style={{ width: `${[70, 55, 90, 45, 60, 50, 40, 65][j % 8]}%` }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <InboxIcon className="size-5 text-ink-muted/60" aria-hidden />
      <p className="font-display text-[0.9375rem] font-semibold text-ink">{title}</p>
      <p className="max-w-sm text-data text-ink-muted">{description}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

/**
 * Failed load. `action` replaces the retry button for errors retrying cannot
 * fix — a 404 is the server's final answer, so the detail screen hands in a
 * "back to the list" link instead of a button that would fail again.
 */
export function ErrorState({
  title = 'Veriler yüklenemedi',
  message,
  onRetry,
  action,
}: {
  title?: string
  message: string
  onRetry?: () => void
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <AlertTriangleIcon className="size-5 text-kordovan" aria-hidden />
      <p className="font-display text-[0.9375rem] font-semibold text-ink">{title}</p>
      <p className="max-w-sm text-data text-ink-muted">{message}</p>
      {action ? (
        <div className="mt-1">{action}</div>
      ) : onRetry ? (
        <Button variant="outline" className="mt-1" onClick={onRetry}>
          <RotateCcwIcon aria-hidden />
          Yeniden dene
        </Button>
      ) : null}
    </div>
  )
}
